import { LitElement, css, html } from 'lit'
import { getProgramTerms } from '../../services/program-terms.js'
import { getProgramSummary } from '../../utils/get-program-summary.js'
import { formatPriceAmount, formatCurrency } from '../../utils/format-price.js'
import { formatTermDate } from '../../utils/format-date.js'
import { scrollToProgramTerms } from '../../utils/program-navigation.js'
import { disableOfferScrollRestoration } from '../../utils/offer-page-lifecycle.js'

/** Cena i konkretne terminy programu, osadzane nad opisem wyjazdu. */
export class CtOfferSummary extends LitElement {
  static properties = {
    programKey: { type: String, attribute: 'program-key' },
    targetId: { type: String, attribute: 'target-id' },
    terms: { state: true },
    loading: { state: true },
    error: { state: true },
  }

  constructor() {
    super()
    this.programKey = ''
    this.targetId = 'ct-terms-start'
    this.terms = []
    this.loading = true
    this.error = ''
    this.loadRequest = 0
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      overflow-anchor: none;
      font-family: Poppins, Arial, sans-serif;
      color: #33404c;
    }

    * { box-sizing: border-box; }

    .frame { padding: 10px 0 32px; }

    @media (min-width: 1200px) {
      .frame { padding-bottom: 48px; }
    }

    .summary {
      display: grid;
      grid-template-columns: minmax(280px, .9fr) minmax(0, 1.5fr) auto;
      align-items: stretch;
      width: 100%;
      max-width: 1200px;
      margin: 0 auto;
      overflow: hidden;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 2px 6px rgba(0, 0, 0, .06);
    }

    .price-block {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 16px 24px;
      background: linear-gradient(120deg, #edf5ed, #f6f9f4);
    }

    .section-label {
      margin: 0 0 6px;
      color: #18586a;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.5;
    }

    .price {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 0 7px;
      color: #398248;
      line-height: 1.25;
    }

    .from { font-size: 18px; font-weight: 600; }
    .amount { font-size: 32px; font-weight: 600; letter-spacing: -.7px; white-space: nowrap; }
    .unit { font-size: 14px; font-weight: 500; white-space: nowrap; }
    .note { margin: 2px 0 0; color: #5e5e5e; font-size: 13px; line-height: 1.4; }
    .price-fallback { font-size: 20px; font-weight: 600; }

    .schedule {
      display: flex;
      min-width: 0;
      flex-direction: column;
      justify-content: center;
      padding: 16px 24px;
    }

    .dates {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .date {
      display: flex;
      max-width: 100%;
      align-items: center;
      gap: 9px;
      padding: 5px 10px;
      border: 1px solid #e4ecec;
      border-radius: 10px;
      background: #f5f8f8;
      font-size: 13px;
      line-height: 1.6;
    }
    .calendar { flex: 0 0 19px; width: 19px; height: 19px; color: #18586a; }

    .action { display: flex; align-items: center; padding: 16px 24px 16px 0; }
    .cta {
      border: 0;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 44px;
      padding: 10px 18px;
      border-radius: 12px;
      background: #f7931e;
      color: #fff;
      font-size: 20px;
      font-weight: 500;
      line-height: 1;
      letter-spacing: normal;
      white-space: nowrap;
      text-decoration: none;
      transition: background 160ms ease, box-shadow 160ms ease;
    }
    .cta:hover { background: #ff9f2e; box-shadow: 0 3px 8px rgba(247, 147, 30, .2); }
    .cta:focus-visible { outline: 3px solid #18586a; outline-offset: 4px; }
    .status { grid-column: 1 / -1; margin: 0; padding: 26px 28px; font-size: 14px; line-height: 1.6; }

    @media (min-width: 1200px) {
      .summary { grid-template-columns: 300px minmax(0, 1fr) auto; }
      .cta { width: 250px; }
    }

    @media (min-width: 769px) and (max-width: 1199px) {
      .cta { font-size: clamp(16px, 2.2vw, 19px); }
    }

    @media (max-width: 1050px) and (min-width: 769px) {
      .summary { grid-template-columns: minmax(270px, .85fr) minmax(0, 1.15fr); }
      .price-block { grid-row: span 2; }
      .schedule { padding: 16px 20px 10px; }
      .action { padding: 0 20px 16px; }
    }

    @media (max-width: 768px) {
      /* Szerokość względem ekranu, niezależna od szerokości bloku WebWave.
       * Wycentrowanie zapobiega sumowaniu odstępów kontenera i komponentu. */
      .frame {
        width: 94vw;
        margin-left: calc(50% - 47vw);
      }
      .summary { grid-template-columns: minmax(0, 1fr); }
      .price-block { padding: 13px 18px 12px; }
      .section-label { margin-bottom: 5px; }
      .amount { font-size: 32px; }
      .schedule { padding: 12px 18px 10px; }
      .dates { flex-direction: column; }
      .date { padding: 4px 10px; font-size: 13px; }
      .action { padding: 0 18px 14px; }
      .cta { width: 100%; font-size: 17px; }
      .status { padding: 22px; }
    }

    @media (max-width: 360px) {
      .price-block { padding: 12px 16px; }
      .schedule { padding: 10px 16px; }
      .action { padding: 0 16px 12px; }
      .amount { font-size: 29px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .cta { transition: none; }
    }
  `

  connectedCallback() {
    this.restoreScrollRestoration = disableOfferScrollRestoration()
    super.connectedCallback()
  }

  disconnectedCallback() {
    this.restoreScrollRestoration?.()
    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('programKey')) this.loadTerms()
  }

  async loadTerms() {
    const request = ++this.loadRequest
    this.loading = true
    this.error = ''
    this.terms = []

    try {
      const result = await getProgramTerms(this.programKey)
      if (request !== this.loadRequest) return
      this.terms = result.terms
      if (!result.program) {
        this.error = 'Nie znaleziono programu wyjazdu.'
      } else if (result.errors.length > 0 && result.terms.length === 0) {
        this.error = 'Nie udało się pobrać terminów. Odśwież stronę i spróbuj ponownie.'
      }
    } catch {
      if (request !== this.loadRequest) return
      this.error = 'Nie udało się pobrać ceny i terminów. Odśwież stronę i spróbuj ponownie.'
    } finally {
      if (request === this.loadRequest) this.loading = false
    }
  }

  handleCtaClick(event) {
    scrollToProgramTerms(event, this.targetId, this.programKey)
  }

  getStickyStartElement() {
    return this.renderRoot?.querySelector('.cta') ?? null
  }

  render() {
    const summary = getProgramSummary(this.terms)
    const terms = this.terms.filter(term => term?.isHidden !== true)
    const status = this.loading
      ? 'Pobieramy cenę i terminy wyjazdu…'
      : this.error || (terms.length === 0 ? 'Obecnie nie ma dostępnych terminów.' : '')

    return html`
      <div class="frame">
        <section class="summary" aria-label="Cena i terminy wyjazdu" aria-busy=${this.loading}>
          ${status ? html`<p class="status" role="status">${status}</p>` : html`
            <div class="price-block">
              <div class="price">
                ${summary.lowestPrice ? html`
                  ${summary.pricesDiffer ? html`<span class="from">Od</span>` : ''}
                  <span class="amount">${formatPriceAmount(summary.lowestPrice.amount)} ${formatCurrency(summary.lowestPrice.currency)}</span>
                  <span class="unit">/ os.</span>
                ` : html`<span class="price-fallback">Sprawdź cenę</span>`}
              </div>
              <p class="note">+ koszt biletów lotniczych</p>
            </div>
            <div class="schedule">
              <p class="section-label">Terminy wyjazdu</p>
              <ul class="dates" aria-label="Terminy wyjazdu">
                ${terms.map(term => html`
                  <li class="date">
                    <svg class="calendar" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="4" y="5" width="16" height="16" rx="2"></rect>
                      <path d="M8 3v4M16 3v4M4 10h16m-12 6 2 2 5-5"></path>
                    </svg>
                    <span>${formatTermDate(term.startDate, term.endDate, term.displayDate || 'Sprawdź termin')}</span>
                  </li>
                `)}
              </ul>
            </div>
            <div class="action">
              <button class="cta" type="button" @click=${this.handleCtaClick}>
                WYBIERZ TERMIN
              </button>
            </div>
          `}
        </section>
      </div>
    `
  }
}

if (!customElements.get('ct-offer-summary')) {
  customElements.define('ct-offer-summary', CtOfferSummary)
}
